import { useEffect, useMemo, useState } from 'react';
import { Badge, Box, Button, Field, Flex, Spinner, Text } from '@optiaxiom/react';
import {
  IconArrowLeft,
  IconArrowRight,
  IconArrowsRotate,
  IconXmark,
} from '@optimizely/axiom-icons';

import { Sidebar } from '@frontend/components';
import { MarkdownText } from '@frontend/components/MarkdownText';
import { useAppCompanionRuleExecutions } from '@frontend/hooks';
import { CompanionRule } from '@frontend/types/app-companion';

import { getEventTypeLabel } from '../constants';

import { EventInformation } from './EventInformation';
import { RuleExecutionCard } from './RuleExecutionCard';

type RuleExecutionsSidebarProps = {
  open: boolean;
  onClose: () => void;
  rule: CompanionRule;
  agentId: string;
};

export function RuleExecutionsSidebar({
  open,
  onClose,
  rule,
  agentId,
}: RuleExecutionsSidebarProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;
  // Reset to page 1 when rule changes
  useEffect(() => {
    setCurrentPage(1);
  }, [rule.id]);

  const {
    data: executionsResponse,
    isLoading: isLoadingExecutions,
    refetch,
  } = useAppCompanionRuleExecutions(agentId, rule.id, currentPage, pageSize);

  const executions = useMemo(() => executionsResponse?.data || [], [executionsResponse?.data]);

  const pagination = useMemo(
    () => executionsResponse?.pagination,
    [executionsResponse?.pagination]
  );

  const handlePreviousPage = () => {
    if (pagination && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination && currentPage < pagination.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <Sidebar open={open} dock="right" initialWidth={450}>
      <Flex flexDirection="column" gap="16" p="16">
        <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
          <Flex flexDirection="row" alignItems="center" gap="12">
            <Text fontWeight="600" fontSize="lg">
              Rule Executions
            </Text>
            <Badge variant="subtle" color="fg.accent">
              {getEventTypeLabel(rule.event_type)}
            </Badge>
          </Flex>
          <Flex flexDirection="row" gap="8">
            <Button
              icon={<IconArrowsRotate size="16" />}
              appearance="subtle"
              onClick={() => refetch()}
              title="Refresh executions"
            />
            <Button icon={<IconXmark size="16" />} appearance="default" onClick={onClose} />
          </Flex>
        </Flex>

        {/* Rule Details */}
        <Field label="Rule Details">
          <Box border="1" borderColor="border.default" rounded="md">
            <Box bg="bg.default" p="12">
              <MarkdownText
                text={`| Field | Value |
|-------|--------|
| **Event Type** | ${getEventTypeLabel(rule.event_type)} |
| **Rule ID** | \`${rule.id}\` |`}
              />
            </Box>
          </Box>
        </Field>

        <Field label="Event Metadata">
          <EventInformation eventType={rule.event_type} />
        </Field>

        {/* Executions List */}
        {isLoadingExecutions ? (
          <Flex justifyContent="center" py="24">
            <Spinner>Loading executions...</Spinner>
          </Flex>
        ) : executions.length === 0 ? (
          <Box py="24" textAlign="center">
            <Text fontSize="sm" color="fg.secondary">
              No executions found for this rule
            </Text>
          </Box>
        ) : (
          <Flex flexDirection="column" gap="12">
            {executions.map((execution, index) => (
              <RuleExecutionCard
                key={execution.id}
                execution={execution}
                defaultOpen={index === 0}
              />
            ))}

            {/* Pagination Controls */}
            {pagination && pagination.totalPages > 1 && (
              <Flex justifyContent="space-between" alignItems="center" mt="8">
                <Button
                  appearance="subtle"
                  size="sm"
                  icon={<IconArrowLeft size="12" />}
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>

                <Text fontSize="sm" color="fg.secondary">
                  Page {currentPage} of {pagination.totalPages} ({pagination.total} total)
                </Text>

                <Button
                  appearance="subtle"
                  size="sm"
                  addonAfter={<IconArrowRight size="12" />}
                  onClick={handleNextPage}
                  disabled={currentPage === pagination.totalPages}
                >
                  Next
                </Button>
              </Flex>
            )}
          </Flex>
        )}
      </Flex>
    </Sidebar>
  );
}
